import { z } from 'zod/v4';

/**
 * Extended position settings shared by every "zero-block" widget variant — the
 * data shape produced by the host app's `webWidgetExtendedPositionSettingsFormGroup`
 * factory (`apps/websites/src/app/shared/components/web-widget-extended-position-settings/`).
 *
 * "Extended" here means **per-device** layout: separate width/height/x/y/rotate/zIndex
 * triples for desktop, tablet, and mobile. The default-variant `horizontalPosition`
 * + `maxWidth` collapse all three devices into a single shorthand; zero-blocks need
 * the full grid so users can pixel-position the widget on each breakpoint.
 *
 * `positionVertical`/`positionHorizontal` are anchor strings ('top'/'left' by default
 * in the host factory) — kept as `z.string()` here rather than enumerated since the
 * host doesn't constrain them at the form-control level.
 */
export const extendedPositionSchemaLegacy = z.object({
    width: z.number(),
    tabletWidth: z.number(),
    mobileWidth: z.number(),

    height: z.number(),
    tabletHeight: z.number(),
    mobileHeight: z.number(),

    x: z.number(),
    tabletX: z.number(),
    mobileX: z.number(),

    y: z.number(),
    tabletY: z.number(),
    mobileY: z.number(),

    isMobilePositionChanged: z.boolean(),
    isTabletPositionChanged: z.boolean(),

    rotate: z.number(),
    tabletRotate: z.number(),
    mobileRotate: z.number(),

    zIndex: z.number(),
    tabletZIndex: z.number(),
    mobileZIndex: z.number(),

    positionVertical: z.string(),
    positionHorizontal: z.string(),
});

export type ExtendedPositionLegacy = z.infer<typeof extendedPositionSchemaLegacy>;

/**
 * Shape fragment for **zero-block** widget data schemas — the extended
 * position grid is required on every zero variant (zero blocks post-date the
 * feature, so a zero-tagged widget without it is a validation error by
 * design). Spread into the data `z.object`. Form twin:
 * `extendedPositionPlugin` in `@sites/entity-signal-form`
 * (`plugins/widget/position.plugin.ts`).
 */
export const extendedPositionPluginShapeLegacy = {
    position: extendedPositionSchemaLegacy,
} as const;

/**
 * Override values for `createExtendedPositionLegacy`. Each field is optional —
 * unspecified entries fall back to the host-app factory defaults.
 */
export type ExtendedPositionOverridesLegacy = Partial<ExtendedPositionLegacy>;

/**
 * Default extended-position bag. Mirrors the host-app form factory defaults
 * verbatim — see `webWidgetExtendedPositionSettingsFormGroup` for the source
 * of truth. Per-widget callers (e.g. video) pass overrides for the size
 * fields that should match the widget's natural aspect ratio.
 */
export const createExtendedPositionLegacy = (
    overrides: ExtendedPositionOverridesLegacy = {}
): ExtendedPositionLegacy => ({
    width: 556,
    tabletWidth: 556,
    mobileWidth: 450,
    height: 160,
    tabletHeight: 160,
    mobileHeight: 225,
    x: 50,
    tabletX: 50,
    mobileX: 50,
    y: 50,
    tabletY: 50,
    mobileY: 50,
    isMobilePositionChanged: false,
    isTabletPositionChanged: false,
    rotate: 0,
    tabletRotate: 0,
    mobileRotate: 0,
    zIndex: 0,
    tabletZIndex: 0,
    mobileZIndex: 0,
    positionVertical: 'top',
    positionHorizontal: 'left',
    ...overrides,
});
